/**
 * Cost Center Hooks (Phase D/E)
 * React Query hooks over the cost_events ledger (/v1/admin/cost/*).
 */

import { useQuery } from '@tanstack/react-query';
import { costCenterApi } from '@/lib/api/costCenter';

const COST_KEYS = {
  all: ['cost-center'] as const,
  byModel: (s?: string, e?: string, r?: string, excludeTeam?: boolean) =>
    [...COST_KEYS.all, 'by-model', { s, e, r, excludeTeam }] as const,
  byAgent: (s?: string, e?: string, r?: string, excludeTeam?: boolean) =>
    [...COST_KEYS.all, 'by-agent', { s, e, r, excludeTeam }] as const,
  totals: (s?: string, e?: string, r?: string, excludeTeam?: boolean) =>
    [...COST_KEYS.all, 'totals', { s, e, r, excludeTeam }] as const,
};

export interface UseCostOptions {
  startDate?: string;
  endDate?: string;
  region?: string;
  excludeTeam?: boolean;
  enabled?: boolean;
}

export function useCostByModel(opts: UseCostOptions = {}) {
  const { startDate, endDate, region, excludeTeam, enabled = true } = opts;
  return useQuery({
    queryKey: COST_KEYS.byModel(startDate, endDate, region, excludeTeam),
    queryFn: () => costCenterApi.getByModel(startDate, endDate, region, excludeTeam),
    enabled,
    staleTime: 30000,
  });
}

export function useCostByAgent(opts: UseCostOptions = {}) {
  const { startDate, endDate, region, excludeTeam, enabled = true } = opts;
  return useQuery({
    queryKey: COST_KEYS.byAgent(startDate, endDate, region, excludeTeam),
    queryFn: () => costCenterApi.getByAgent(startDate, endDate, region, excludeTeam),
    enabled,
    staleTime: 30000,
  });
}

export function useCostTotals(opts: UseCostOptions = {}) {
  const { startDate, endDate, region, excludeTeam, enabled = true } = opts;
  return useQuery({
    queryKey: COST_KEYS.totals(startDate, endDate, region, excludeTeam),
    queryFn: () => costCenterApi.getTotals(startDate, endDate, region, excludeTeam),
    enabled,
    staleTime: 30000,
  });
}
